// Admin API service for managing vendedores, partners, clientes, and comissões
import { api } from './api'

// ============================================================================
// VENDEDORES TYPES
// ============================================================================

export interface Vendedor {
    id: string
    nome: string
    email: string
    telefone: string | null
    nif: string | null
    iban: string | null
    comissao_percentagem: number
    ativo: boolean
    notas: string | null
    created_at: string
    updated_at: string
}

export interface VendedorResumo extends Vendedor {
    total_partners: number
    total_clientes: number
    comissao_pendente: number
    comissao_paga: number
}

export interface CreateVendedorData {
    nome: string
    email: string
    telefone?: string
    nif?: string
    iban?: string
    comissao_percentagem?: number
    notas?: string
}

export interface UpdateVendedorData {
    nome?: string
    email?: string
    telefone?: string
    nif?: string
    iban?: string
    comissao_percentagem?: number
    ativo?: boolean
    notas?: string
}

export interface VendedorListResponse {
    vendedores: VendedorResumo[]
    total: number
}

// ============================================================================
// PARTNERS TYPES
// ============================================================================

export interface Partner {
    id: string
    vendedor_id: string | null
    nome: string
    nif: string | null
    email: string | null
    telefone: string | null
    morada: string | null
    iban: string | null
    comissao_percentagem: number
    ativo: boolean
    notas: string | null
    created_at: string
    updated_at: string
}

export interface PartnerResumo extends Partner {
    vendedor_nome: string | null
    total_clientes: number
    clientes_ativos: number
    comissao_pendente: number
    comissao_paga: number
}

export interface CreatePartnerData {
    vendedor_id?: string | null
    nome: string
    nif?: string
    email?: string
    telefone?: string
    morada?: string
    iban?: string
    comissao_percentagem?: number
    notas?: string
}

export interface UpdatePartnerData {
    vendedor_id?: string | null
    nome?: string
    nif?: string
    email?: string
    telefone?: string
    morada?: string
    iban?: string
    comissao_percentagem?: number
    ativo?: boolean
    notas?: string
}

export interface PartnerListResponse {
    partners: PartnerResumo[]
    total: number
}

// ============================================================================
// CLIENTES TYPES
// ============================================================================

export interface ClienteInfo {
    user_id: number
    name: string
    company_name: string | null
    nif: string
    email: string | null
    subscription_status: string | null
    plan_id: string | null
    subscription_end_date: string | null
    partner_id: string | null
    partner_nome: string | null
    vendedor_id: string | null
    vendedor_nome: string | null
    created_at: string
}

export interface ClienteListResponse {
    clientes: ClienteInfo[]
    total: number
}

export interface ClienteFilter {
    plan?: string
}

export interface AssignVendedorData {
    vendedor_id: string | null
}

// ============================================================================
// COMISSOES TYPES
// ============================================================================

export interface Comissao {
    id: string
    tipo: 'vendedor' | 'partner'
    beneficiario_id: string
    beneficiario_nome: string | null
    user_id: number
    subscription_id: string | null
    mes_referencia: string
    valor_subscricao: number
    percentagem: number
    valor_comissao: number
    estado: 'pendente' | 'pago'
    data_pagamento: string | null
    referencia_pagamento: string | null
    created_at: string
}

export interface ComissaoAPagar {
    tipo: string
    beneficiario_id: string
    nome: string | null
    nif: string | null
    iban: string | null
    total_pendente: number
    count: number
}

export interface ComissaoResumoMensal {
    mes_referencia: string
    tipo: string
    total_comissoes: number
    total_valor: number
    valor_pendente: number
    valor_pago: number
}

export interface ComissaoListResponse {
    comissoes: Comissao[]
    total: number
}

export interface ComissaoSummaryResponse {
    total_pendente: number
    total_pago: number
    total_mes: number
    resumo_mensal: ComissaoResumoMensal[]
}

export interface MarkAsPaidData {
    ids: string[]
    data_pagamento: string
    referencia_pagamento?: string
}

export interface MarkAsPaidResponse {
    success: boolean
    count: number
    message: string
}

export interface ComissaoFilter {
    mes?: string
    tipo?: string
    estado?: string
}

// ============================================================================
// ADMIN API
// ============================================================================

export const adminApi = {
    // VENDEDORES
    async listVendedores(): Promise<VendedorListResponse> {
        return api.get<VendedorListResponse>('/api/admin/vendedores')
    },

    async getVendedor(id: string): Promise<Vendedor> {
        return api.get<Vendedor>(`/api/admin/vendedores/${id}`)
    },

    async createVendedor(data: CreateVendedorData): Promise<Vendedor> {
        return api.post<Vendedor>('/api/admin/vendedores', data)
    },

    async updateVendedor(id: string, data: UpdateVendedorData): Promise<Vendedor> {
        return api.put<Vendedor>(`/api/admin/vendedores/${id}`, data)
    },

    async deleteVendedor(id: string): Promise<{ success: boolean; message: string }> {
        return api.delete<{ success: boolean; message: string }>(`/api/admin/vendedores/${id}`)
    },

    async getVendedorPartners(id: string): Promise<Partner[]> {
        return api.get<Partner[]>(`/api/admin/vendedores/${id}/partners`)
    },

    async getVendedorComissoes(id: string): Promise<Comissao[]> {
        return api.get<Comissao[]>(`/api/admin/vendedores/${id}/comissoes`)
    },

    // PARTNERS
    async listPartners(): Promise<PartnerListResponse> {
        return api.get<PartnerListResponse>('/api/admin/partners')
    },

    async getPartner(id: string): Promise<Partner> {
        return api.get<Partner>(`/api/admin/partners/${id}`)
    },

    async createPartner(data: CreatePartnerData): Promise<Partner> {
        return api.post<Partner>('/api/admin/partners', data)
    },

    async updatePartner(id: string, data: UpdatePartnerData): Promise<Partner> {
        return api.put<Partner>(`/api/admin/partners/${id}`, data)
    },

    async deletePartner(id: string): Promise<{ success: boolean; message: string }> {
        return api.delete<{ success: boolean; message: string }>(`/api/admin/partners/${id}`)
    },

    async getPartnerClientes(id: string): Promise<ClienteInfo[]> {
        return api.get<ClienteInfo[]>(`/api/admin/partners/${id}/clientes`)
    },

    async getPartnerComissoes(id: string): Promise<Comissao[]> {
        return api.get<Comissao[]>(`/api/admin/partners/${id}/comissoes`)
    },

    // CLIENTES
    async listClientes(): Promise<ClienteListResponse> {
        return api.get<ClienteListResponse>('/api/admin/clientes')
    },

    async listClientesDiretos(): Promise<ClienteListResponse> {
        return api.get<ClienteListResponse>('/api/admin/clientes/diretos')
    },

    async listAllClientes(filter?: ClienteFilter): Promise<ClienteListResponse> {
        const params = new URLSearchParams()
        if (filter?.plan) params.append('plan', filter.plan)
        const queryString = params.toString()
        const url = queryString ? `/api/admin/clientes/all?${queryString}` : '/api/admin/clientes/all'
        return api.get<ClienteListResponse>(url)
    },

    async assignVendedor(userId: number, data: AssignVendedorData): Promise<{ success: boolean; message: string }> {
        return api.put<{ success: boolean; message: string }>(`/api/admin/clientes/${userId}/atribuir-vendedor`, data)
    },

    // COMISSOES
    async listComissoes(filter?: ComissaoFilter): Promise<ComissaoListResponse> {
        const params = new URLSearchParams()
        if (filter?.mes) params.append('mes', filter.mes)
        if (filter?.tipo) params.append('tipo', filter.tipo)
        if (filter?.estado) params.append('estado', filter.estado)
        const queryString = params.toString()
        const url = queryString ? `/api/admin/comissoes?${queryString}` : '/api/admin/comissoes'
        return api.get<ComissaoListResponse>(url)
    },

    async getComissoesSummary(): Promise<ComissaoSummaryResponse> {
        return api.get<ComissaoSummaryResponse>('/api/admin/comissoes/resumo')
    },

    async getComissoesAPagar(): Promise<ComissaoAPagar[]> {
        return api.get<ComissaoAPagar[]>('/api/admin/comissoes/a-pagar')
    },

    async markComissoesPaid(data: MarkAsPaidData): Promise<MarkAsPaidResponse> {
        return api.put<MarkAsPaidResponse>('/api/admin/comissoes/marcar-pago', data)
    },

    async exportComissoes(filter?: ComissaoFilter): Promise<ComissaoListResponse> {
        const params = new URLSearchParams()
        if (filter?.mes) params.append('mes', filter.mes)
        if (filter?.tipo) params.append('tipo', filter.tipo)
        if (filter?.estado) params.append('estado', filter.estado)
        const queryString = params.toString()
        const url = queryString ? `/api/admin/comissoes/exportar?${queryString}` : '/api/admin/comissoes/exportar'
        return api.get<ComissaoListResponse>(url)
    }
}
